import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, switchMap, tap, throwError } from 'rxjs';
import { API_BASE_URL } from './api.config';
import { safeAuthReturnUrl } from './auth-return-url';
import { AuthSessionStore } from './auth-session.store';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const AUTHENTICATION_PROBES = [
  '/api/v1/auth/login',
  '/api/v1/auth/me',
  '/api/v1/auth/forgot-password',
  '/api/v1/auth/reset-password',
];
const CHANGE_PASSWORD_PATH = '/api/v1/auth/change-password';

function responseCode(error: HttpErrorResponse): string | null {
  const payload = error.error;

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const meta = record['meta'];
  const errors = record['errors'];
  const candidate =
    record['code'] ??
    (meta && typeof meta === 'object' ? (meta as Record<string, unknown>)['code'] : null) ??
    (errors && typeof errors === 'object'
      ? (errors as Record<string, unknown>)['code']
      : null);

  if (Array.isArray(candidate)) {
    return typeof candidate[0] === 'string' ? candidate[0] : null;
  }

  return typeof candidate === 'string' ? candidate : null;
}

const XSRF_PREFIX = 'XSRF-TOKEN=';

/** The XSRF token that last carried an unsafe request through, if any. */
let workingToken: string | null = null;

/** Forgets the token that last worked. For tests. */
export function forgetWorkingXsrfToken(): void {
  workingToken = null;
}

function decoded(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Every XSRF token the page can read, the likeliest first.
 *
 * A parent domain's cookie of the same name is readable too: the live site
 * sets its XSRF-TOKEN for the whole of gulfsquads.com, so on the testing site
 * the browser shows two, and the live one came first and failed every
 * sign-in (found 2026-10-04). Nothing in a cookie says which domain it is
 * for, so the token that last worked leads, and the others are tried after a
 * refusal.
 */
export function xsrfTokens(cookies: string, preferred: string | null = null): readonly string[] {
  const values = cookies
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part.startsWith(XSRF_PREFIX))
    .map((part) => decoded(part.slice(XSRF_PREFIX.length)))
    .filter((value) => value !== '');
  const unique = [...new Set(values)];

  return preferred !== null && unique.includes(preferred)
    ? [preferred, ...unique.filter((value) => value !== preferred)]
    : unique;
}

function readXsrfTokens(): readonly string[] {
  return typeof document === 'undefined' ? [] : xsrfTokens(document.cookie, workingToken);
}

function belongsToApi(url: string, apiBaseUrl: string): boolean {
  try {
    return new URL(url).origin === new URL(apiBaseUrl).origin;
  } catch {
    return false;
  }
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const apiBaseUrl = inject(API_BASE_URL);

  if (!belongsToApi(request.url, apiBaseUrl)) {
    return next(request);
  }

  const session = inject(AuthSessionStore);
  const router = inject(Router);
  const csrfClient = new HttpClient(inject(HttpBackend));
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  };
  if (!request.headers.has('Accept')) {
    headers['Accept'] = 'application/json';
  }
  const unsafe = !SAFE_METHODS.has(request.method);
  const csrfToken = unsafe ? (readXsrfTokens()[0] ?? null) : null;

  if (csrfToken) {
    headers['X-XSRF-TOKEN'] = csrfToken;
  }

  const authenticatedRequest = request.clone({
    withCredentials: true,
    setHeaders: headers,
  });
  /** Once a token carries an unsafe request through, it leads the next time. */
  const remember = (token: string | null) => tap({
    next: (event: HttpEvent<unknown>) => {
      if (token && event instanceof HttpResponse) workingToken = token;
    },
  });

  const handleFailure = (error: unknown) => {
    const code = error instanceof HttpErrorResponse ? responseCode(error) : null;
    const isSessionFailure =
      error instanceof HttpErrorResponse &&
      (error.status === 401 || error.status === 419 || code === 'SESSION_EXPIRED');
    const isInactiveAccount =
      error instanceof HttpErrorResponse &&
      error.status === 403 &&
      (code === 'ACCOUNT_INACTIVE' ||
        (error.error as { message?: unknown } | null)?.message === 'Account is inactive.');
    const isPasswordChangeRequired =
      error instanceof HttpErrorResponse &&
      error.status === 428 &&
      code === 'PASSWORD_CHANGE_REQUIRED';
    const isAuthenticationProbe = AUTHENTICATION_PROBES.some((path) =>
      request.url.includes(path),
    );

    if (isSessionFailure || isInactiveAccount) {
      session.clear();
    }

    if (
      (isSessionFailure || isInactiveAccount) &&
      !isAuthenticationProbe &&
      !router.url.startsWith('/login')
    ) {
      const returnUrl = router.url.startsWith('/') ? router.url : '/dashboard';
      void router.navigate(['/login'], {
        queryParams: isInactiveAccount
          ? { reason: 'inactive' }
          : { reason: 'session-expired', returnUrl },
      });
    }

    if (isPasswordChangeRequired) {
      const user = session.user();

      if (user && !user.must_change_password) {
        session.setUser({ ...user, must_change_password: true });
      }

      if (
        !request.url.includes(CHANGE_PASSWORD_PATH) &&
        !router.url.startsWith('/change-password')
      ) {
        void router.navigate(['/change-password'], {
          queryParams: { returnUrl: safeAuthReturnUrl(router.url) },
        });
      }
    }

    return throwError(() => error);
  };

  /**
   * After a refusal and a fresh cookie, each token not yet refused, in turn,
   * until one is taken; then the refusal stands.
   */
  const retry = (tokens: readonly string[], refusal: unknown): Observable<HttpEvent<unknown>> => {
    const [token, ...rest] = tokens;

    if (token === undefined) {
      return handleFailure(refusal);
    }

    return next(authenticatedRequest.clone({ setHeaders: { 'X-XSRF-TOKEN': token } })).pipe(
      remember(token),
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 419 && rest.length > 0
          ? retry(rest, error)
          : handleFailure(error),
      ),
    );
  };

  return next(authenticatedRequest).pipe(
    remember(csrfToken),
    catchError((error: unknown) => {
      const canRefreshCsrf =
        error instanceof HttpErrorResponse &&
        error.status === 419 &&
        unsafe;

      if (!canRefreshCsrf) {
        return handleFailure(error);
      }

      return csrfClient
        .get<void>(`${apiBaseUrl.replace(/\/$/, '')}/sanctum/csrf-cookie`, {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        .pipe(
          catchError(() => handleFailure(error)),
          switchMap(() => {
            // A token just refused is not tried again: a refreshed cookie has a new value. Three at most.
            const cookies = typeof document === 'undefined' ? '' : document.cookie;
            const fresh = xsrfTokens(cookies).filter((token) => token !== csrfToken).slice(0, 3);

            return fresh.length > 0 ? retry(fresh, error) : handleFailure(error);
          }),
        );
    }),
  );
};
